<template>
    <div class="core-number-input-wrapper" :style="styles">
        <InputNumber
            v-model="inputValue"
            :locale="numberFormat()"
            :name="name"
            :inputStyle="{ 'text-align': 'right' }"
            :placeholder="placeholder"
            :class="classes"
            :style="styles"
            :suffix="suffix"
            :minFractionDigits="minFractionDigits"
            :maxFractionDigits="maxFractionDigits"
            :disabled="disabled"
            @input="onInput"
            @focus="onFocus()"
            @blur="onBlur()"
        />

        <CoreInputNotification
            v-if="isFocused || customNotificationMessage"
            :validator="validator"
            :customNotificationMessage="customNotificationMessage"
        />
    </div>
</template>

<script>
import { useUserStore } from '@/stores/user'
import InputNumber from 'primevue/inputnumber'
import CoreInputNotification from './CoreInputNotification.vue'
import { numberFormatPlaceholder } from '@/helpers/localeOptions'

export default {
    name: 'CoreNumberInput',

    components: { InputNumber, CoreInputNotification },

    props: {
        modelValue: {
            type: [Number, String],
            default: null,
        },
        name: {
            type: String,
            default: '',
        },
        suffix: {
            type: String,
            default: null,
        },
        width: {
            type: Number,
            default: null,
        },
        minFractionDigits: {
            type: Number,
            default: undefined,
        },
        maxFractionDigits: {
            type: Number,
            default: undefined,
        },
        validator: {
            type: Object,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        customNotificationMessage: {
            type: Object,
            default: null,
        },
    },

    emits: ['update:modelValue', 'focus', 'blur'],

    data() {
        return {
            userStore: useUserStore(),
            isFocused: false,
        }
    },

    mounted() {
        if (this.validator) {
            if (this.modelValue === null || this.modelValue === '') {
                this.validator.$reset()
            } else {
                this.validator.$touch()
            }
        }
    },

    computed: {
        inputValue: {
            get() {
                return this.modelValue
            },
            set(value) {
                this.$emit('update:modelValue', value)

                if (this.validator) {
                    this.validator.$touch()
                }
            },
        },

        styles() {
            const stylesObject = {}

            if (this.width !== null && typeof this.width === 'number') {
                stylesObject.width = `${this.width}px`
            }

            return stylesObject
        },

        classes() {
            return {
                'core-number-input': true,
                'core-valid': this.isValid,
                'core-invalid': this.isInvalid,
                'core-warning': this.isWarning,
            }
        },

        isValid() {
            if (this.validator !== null && !this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isInvalid() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'error'
            ) {
                return true
            }

            if (this.validator !== null && this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isWarning() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'warn'
            ) {
                return true
            }

            return false
        },

        validatorErrors() {
            if (this.validator?.$error === undefined) {
                return {}
            }
            return this.validator.$error
        },

        validatorParams() {
            if (this.validator?.$params === undefined) {
                return {}
            }
            return this.validator.$params
        },

        placeholder() {
            const placeholder_tk_name = numberFormatPlaceholder(
                this.userStore.userData.preferred_number_format
            )

            return this.$t(placeholder_tk_name)
        },
    },

    methods: {
        numberFormat() {
            // Implement the number format logic here
            return 'en-US' // Default fallback
        },

        // temp solution to test users feedback
        // this will fix default prime-vue behavior to update state on every input change instead of only on blur
        onInput(event) {
            this.inputValue = event.value
        },

        onFocus() {
            this.isFocused = true
            this.$emit('focus')
        },

        onBlur() {
            this.isFocused = false
            this.$emit('blur')
        },
    },
}
</script>

<style>
.core-number-input-wrapper {
    position: relative;
    width: 100%;

    & > span {
        width: 100%;
    }

    & .p-inputtext.p-component.p-inputnumber-input {
        font-size: var(--text-base);
    }

    & > span > input {
        width: 100%;
    }

    & > span > input:disabled {
        background-color: var(--color-disabled-bg);
        color: var(--text-disabled);
    }

    & .core-number-input.core-valid input,
    & .core-number-input.core-valid input:focus,
    & .core-number-input.core-valid input:hover {
        border-color: var(--color-success);
    }

    & .core-number-input.core-invalid input,
    & .core-number-input.core-invalid input:focus,
    & .core-number-input.core-invalid input:hover {
        border-color: var(--color-error);
    }

    & .core-number-input.core-warning input,
    & .core-number-input.core-warning input:focus,
    & .core-number-input.core-warning input:hover {
        border-color: var(--color-warning);
    }
}
</style>
